// M+3.6 C7 — unified colour picker. Used by both the theme editor and the
// taxonomy meta editor so the two surfaces feel consistent.
//
// value can be either a hex literal ("#34d399") or a CSS var-name string
// ("var(--gn)"). The mode toggle lets the user pick between the two
// representations; the initial mode auto-detects from the incoming value.
//
//   mode="hex-only"  — only the hex picker is shown (theme editor itself,
//                      where storing var-names would be circular).
//   mode="both"      — hex picker + theme-var dropdown (taxonomy meta).

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useAnchoredMenu } from "../hooks/useAnchoredMenu"; // 135 §3(b)
import { varLabel } from "../lib/themeDerivation";
import { useTranslation } from "../i18n";
import { CT, RAD, FS, Z } from "../lib/tokens";

const VAR_RE = /^var\(\s*(--[a-zA-Z0-9_-]+)\s*\)$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function parseValue(v: any) {
  if (typeof v !== "string")
    return { kind: "hex", hex: CT.c_888888, varName: "" };
  const m = v.trim().match(VAR_RE);
  if (m) return { kind: "var", hex: CT.c_888888, varName: m[1] };
  if (HEX_RE.test(v.trim())) return { kind: "hex", hex: v.trim(), varName: "" };
  return { kind: "hex", hex: CT.c_888888, varName: "" };
}

// Resolve a CSS variable to its current value on document.documentElement.
// Used to colour the swatch when the value is a var-name reference.
function resolveVar(varName: string) {
  if (typeof window === "undefined" || !varName) return null;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return v || null;
}

type ColorOrVarPickerProps = {
  value?: any;
  onChange: (v: string) => void;
  themeVars?: any[];
  mode?: string;
  label?: any;
  disabled?: boolean;
  swatchSize?: number;
};

export default function ColorOrVarPicker({
  value,
  onChange,
  themeVars = [],
  mode = "both",
  label,
  disabled = false,
  swatchSize = 32,
}: ColorOrVarPickerProps) {
  const { t } = useTranslation();
  const initial = useMemo(() => parseValue(value), [value]);
  const [tab, setTab] = useState(
    initial.kind === "var" && mode === "both" ? "var" : "hex",
  );

  useEffect(() => {
    // Re-sync tab when the parent flips between var/hex externally.
    const p = parseValue(value);
    if (mode === "hex-only") {
      setTab("hex");
      return;
    }
    setTab(p.kind);
  }, [value, mode]);

  const parsed = parseValue(value);
  const resolved =
    parsed.kind === "var"
      ? resolveVar(parsed.varName) || CT.c_888888
      : parsed.hex;

  const [varOpen, setVarOpen] = useState(false);
  const varBtnRef = useRef<HTMLButtonElement>(null);
  const varMenuRef = useRef<HTMLDivElement | null>(null);
  const openVarMenu = () => setVarOpen(true);

  /* ── 135 §3(b) — THE RECT-IN-STATE SNAPSHOT IS GONE ───────────────────────
   *
   * What stood here captured `getBoundingClientRect()` INTO STATE on open and
   * handed the numbers to React as inline styles. That is the shape behind the
   * 2026-08-03 production defect — a menu at the bottom-left of the modal
   * instead of under its field — for two reasons `useAnchoredMenu` documents in
   * full: a rect read before layout is all zeros and a zero rect is truthy, and
   * a `position: fixed` child of a ZOOMED root expresses top/left in the zoomed
   * space while `getBoundingClientRect` answers in screen space. This app scales
   * its whole UI with zoom, so the second one is live for every user not at the
   * default size. 134 §1 built the hook to hold this once; §3(b) moves this
   * site onto it.
   *
   * THE PLACEMENT IS NOW MEASURED RATHER THAN ESTIMATED. The flip decision used
   * `Math.min(themeVars.length, 6) * 28 + 4` — a guess at the menu's height
   * from a guess at its row height. The hook flips on `el.offsetHeight`, which
   * is the height the browser actually laid out.
   *
   * `minWidth: 200` IS THE SAME NUMBER, and the hook applies it the same way:
   * `Math.max(anchor.width, minWidth)`, which is exactly what `minW` was.
   *
   * ONE BEHAVIOUR CHANGES, DELIBERATELY: a window resize used to RE-PLACE this
   * menu and now DISMISSES it, which is what `FieldSelect` and `MatterPicker`
   * already do. Three portalled menus answering a resize three ways is the
   * divergence this hook exists to end, and dismissing is the posture 134 §1
   * ruled. Scroll, which this site tracked in no way at all, now re-places and
   * dismisses only when the trigger leaves view. */
  useAnchoredMenu({
    open: varOpen,
    anchorRef: varBtnRef,
    menuRef: varMenuRef,
    onDismiss: () => setVarOpen(false),
    deps: [themeVars.length],
    minWidth: 200,
  });

  /* Everything the hook does not write. `left`/`top`/`width` are its, and the
     menu starts hidden because a menu that paints at the origin for one frame
     is the defect the measurement exists to prevent. */
  const VAR_MENU: CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    visibility: "hidden",
    maxHeight: 220,
    overflowY: "auto",
    background: "var(--bc)",
    border: "1px solid var(--br)",
    borderRadius: RAD.r6,
    /* 135 §3(a)'s token, in the second menu of the same family — `rgba(0,0,0,.45)`
       stood here, a third strength for the same shadow. */
    boxShadow: "0 6px 20px var(--shd)",
    zIndex: Z.top,
  };

  const handleHexChange = (hex: string) => {
    if (!hex) return;
    onChange(hex);
  };
  const handleVarChange = (varName: string) => {
    if (!varName) return;
    onChange(`var(${varName})`);
  };

  const swatchStyle: CSSProperties = {
    display: "inline-block",
    width: swatchSize,
    height: swatchSize,
    borderRadius: RAD.r6,
    border: "1px solid var(--br)",
    background: parsed.kind === "var" ? `var(${parsed.varName})` : parsed.hex,
    flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={swatchStyle}
        title={
          parsed.kind === "var"
            ? `var(${parsed.varName}) → ${resolved}`
            : parsed.hex
        }
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {label && (
          <span style={{ fontSize: FS.s10_5, color: "var(--ts)" }}>{label}</span>
        )}
        {mode === "both" && (
          <div style={{ display: "inline-flex", gap: 2, fontSize: FS.s10 }}>
            {/* 188 (7) — A BORDERED TOGGLE WITH DISABLED SEMANTICS, left
                deliberately: two standalone r4 buttons, each with its own
                border, transparent when off, each with a `disabled` of its
                own. SegmentedControl is a joined pill and this is not one;
                S3c's button pass takes these as buttons. No later pass reads
                "tab" here and converts it. Measured in
                docs/188-switcher-strips.md. */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => setTab("hex")}
              style={{
                padding: "2px 8px",
                borderRadius: RAD.r4,
                border: "1px solid var(--br)",
                background: tab === "hex" ? "var(--as)" : "transparent",
                color: tab === "hex" ? "var(--ac)" : "var(--ts)",
                cursor: disabled ? "default" : "pointer",
                fontFamily: "inherit",
                fontSize: FS.s10,
              }}
            >
              {t("colorPicker.custom_hex", { fallback: "Custom hex" })}
            </button>
            {/* 188 (7) — the second half of the bordered toggle above: the
                same shape, with its own `disabled` and an opacity when the
                theme has no vars. Left deliberately for the same reason —
                SegmentedControl is a joined pill and this is not one. */}
            <button
              type="button"
              disabled={disabled || themeVars.length === 0}
              onClick={() => setTab("var")}
              style={{
                padding: "2px 8px",
                borderRadius: RAD.r4,
                border: "1px solid var(--br)",
                background: tab === "var" ? "var(--as)" : "transparent",
                color: tab === "var" ? "var(--ac)" : "var(--ts)",
                cursor:
                  disabled || themeVars.length === 0 ? "default" : "pointer",
                fontFamily: "inherit",
                fontSize: FS.s10,
                opacity: themeVars.length === 0 ? 0.5 : 1,
              }}
            >
              {t("colorPicker.theme_color", { fallback: "Theme color" })}
            </button>
          </div>
        )}
        {tab === "hex" && (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input
              type="color"
              disabled={disabled}
              value={
                parsed.kind === "hex"
                  ? parsed.hex
                  : resolved.startsWith("#") && HEX_RE.test(resolved)
                    ? resolved
                    : CT.c_888888
              }
              onChange={(e) => handleHexChange(e.target.value)}
              style={{
                width: 32,
                height: 32,
                padding: 1,
                background: "none",
                border: "1px solid var(--br)",
                borderRadius: RAD.r5,
                cursor: disabled ? "default" : "pointer",
              }}
            />
            <input
              type="text"
              disabled={disabled}
              value={parsed.kind === "hex" ? parsed.hex : ""}
              placeholder={CT.c_34d399}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (HEX_RE.test(v)) handleHexChange(v);
                else if (v === "") {
                } // typing in progress
                else handleHexChange(v); // let parent decide; sanitiser may reject
              }}
              className="field-input"
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: FS.s10,
                padding: "2px 6px",
                width: 100,
              }}
            />
          </div>
        )}
        {tab === "var" && mode === "both" && (
          <div style={{ position: "relative" }}>
            <button
              ref={varBtnRef}
              type="button"
              disabled={disabled || themeVars.length === 0}
              onClick={() => (varOpen ? setVarOpen(false) : openVarMenu())}
              className="field-input"
              style={{
                padding: "2px 6px",
                fontSize: FS.s11,
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                cursor:
                  disabled || themeVars.length === 0 ? "default" : "pointer",
                textAlign: "left",
                background: "var(--bi)",
                color: "var(--tp)",
              }}
            >
              {parsed.kind === "var" ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: RAD.r3,
                      border: "1px solid var(--br)",
                      background: `var(${parsed.varName})`,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1 }}>{varLabel(parsed.varName, t)}</span>
                </>
              ) : (
                <span style={{ flex: 1, color: "var(--tm)" }}>
                  {t("colorPicker.pick_a_theme_color", {
                    fallback: "— pick a theme color —",
                  })}
                </span>
              )}
              <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>▾</span>
            </button>
            {varOpen &&
              createPortal(
                <>
                  <div
                    style={{ position: "fixed", inset: 0, zIndex: Z.topMenu }}
                    onClick={() => setVarOpen(false)}
                  />
                  <div ref={varMenuRef} style={VAR_MENU}>
                    {themeVars.map((vn) => {
                      const on = parsed.kind === "var" && parsed.varName === vn;
                      return (
                        <button
                          key={vn}
                          type="button"
                          onClick={() => {
                            handleVarChange(vn);
                            setVarOpen(false);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            padding: "5px 8px",
                            fontSize: FS.s11,
                            background: on ? "var(--as)" : "transparent",
                            color: on ? "var(--ac)" : "var(--ts)",
                            border: "none",
                            cursor: "pointer",
                            textAlign: "left",
                            fontFamily: "inherit",
                          }}
                          onMouseEnter={(e) =>
                            !on &&
                            (e.currentTarget.style.background = "var(--bh)")
                          }
                          onMouseLeave={(e) =>
                            !on &&
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 14,
                              height: 14,
                              borderRadius: RAD.r3,
                              border: "1px solid var(--br)",
                              background: `var(${vn})`,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ flex: 1 }}>{varLabel(vn, t)}</span>
                          <span
                            style={{
                              fontSize: FS.s9,
                              color: "var(--tm)",
                              fontFamily: "'JetBrains Mono',monospace",
                            }}
                          >
                            {vn}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>,
                document.body,
              )}
          </div>
        )}
      </div>
    </div>
  );
}

// Convenience: the var set surfaced in the "Theme color" tab.
// Foreground/text variant — solid accent + status colors. Used for any
// "color" or "iconColor" / "nameColor" field on a taxonomy entry.
// Surface and text vars are intentionally excluded — picking them
// produces invisible or unreadable pills.
export const THEME_PICKER_VARS = ["--ac", "--pp", "--gn", "--am", "--rd"];

// Background variant — alpha-shaded versions of the same accent + status
// colors. Used for any "bgColor" field on a taxonomy entry. These render
// as translucent pill backgrounds beneath solid foreground colors.
export const THEME_PICKER_BG_VARS = ["--ag", "--ps", "--gs", "--ams", "--rs"];
